import { useCallback, useState } from "react";

import * as Method from "./method";

import type * as Type from "./type";
import type * as Repository from "../repository";

type Props = {
	queryData: Repository.Query.Type.MagazineTagQueryData;
	tagList?: Array<string>;
	regionTag?: string;
	prefectureTag?: string;
	cityTag?: string;
};

export const useViewModel = ({ queryData, tagList, regionTag, cityTag, prefectureTag }: Props) => {
	/**
	 * タグの選択上限は3つまで
	 */
	const onCanSelectTag = useCallback(
		(selectedTagList: Array<string>) => !Method.hasListThreeMoreItem(selectedTagList),
		[],
	);

	const [viewModel, setViewModel] = useState<Type.ViewModel>({
		selectedTagList: tagList ?? [],
		selectedRegionTag: regionTag,
		selectedPrefectureTag: prefectureTag,
		selectedCityTag: cityTag,
		canSelectTag: onCanSelectTag(tagList ?? []),
	});

	/**
	 * タグ選択時の処理
	 */
	const onSetSelectedTag = useCallback(
		(tag: string) => {
			setViewModel((prev) => {
				const selectedTagList = [...prev.selectedTagList, tag];
				const canSelectTag = onCanSelectTag(selectedTagList);

				return {
					...prev,
					selectedTagList,
					canSelectTag,
				};
			});
		},
		[onCanSelectTag],
	);
	/**
	 * タグ削除時の処理
	 */
	const onRemoveSelectedTag = useCallback(
		(tag: string) => {
			setViewModel((prev) => {
				const selectedTagList = prev.selectedTagList.filter((id) => id !== tag);
				const canSelectTag = onCanSelectTag(selectedTagList);

				return {
					...prev,
					selectedTagList,
					canSelectTag,
				};
			});
		},
		[onCanSelectTag],
	);
	/**
	 * タグの一括削除時の処理
	 */
	const onRemoveAllSelectedTag = useCallback(() => {
		setViewModel((prev) => ({
			...prev,
			selectedTagList: [],
			canSelectTag: onCanSelectTag([]),
		}));
	}, [onCanSelectTag]);

	/**
	 * 都道府県タグから地域タグを取得
	 */
	const onGetRegionByPrefectureTag = useCallback(
		(prefectureTag: string) =>
			queryData.magazineTagLocationTag.list.find(({ children }) =>
				children.some(({ tag }) => tag === prefectureTag),
			)?.tag ?? "",
		[queryData.magazineTagLocationTag.list],
	);
	/**
	 * 地域/都道府県タグ選択時の処理
	 */
	const onSetSelectedRegionWithPrefectureTag = useCallback(
		(prefectureTag: string) => {
			const regionTag = onGetRegionByPrefectureTag(prefectureTag);
			setViewModel((prev) => ({
				...prev,
				selectedRegionTag: regionTag,
				selectedPrefectureTag: prefectureTag,
			}));
		},
		[onGetRegionByPrefectureTag],
	);
	/**
	 * 都市タグ選択時の処理
	 */
	const onSetSelectedCityTag = useCallback((tag: string) => {
		setViewModel((prev) => ({
			...prev,
			selectedCityTag: tag,
		}));
	}, []);
	/**
	 * 地名タグの一括削除時の処理
	 */
	const onRemoveLocationTag = useCallback(() => {
		setViewModel((prev) => ({
			...prev,
			selectedRegionTag: undefined,
			selectedPrefectureTag: undefined,
			selectedCityTag: undefined,
		}));
	}, []);

	return {
		viewModel,
		// タグ選択時の処理
		onSetSelectedTag,
		// タグ削除時の処理
		onRemoveSelectedTag,
		// タグの一括削除時の処理
		onRemoveAllSelectedTag,
		// 都道府県タグ選択時の処理
		onSetSelectedRegionWithPrefectureTag,
		// 都市タグ選択時の処理
		onSetSelectedCityTag,
		// 地名タグの一括削除時の処理
		onRemoveLocationTag,
	};
};
